import {
  Component,
  ViewChild,
  computed,
  signal,
  ChangeDetectionStrategy,
  effect,
  untracked,
  DestroyRef,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Modal } from 'flowbite';
import { VehicleScheduleComponent } from 'src/app/components/vehicles';
import { normalizeViewOptions } from 'src/app/components/vehicles/scheduleViewOptions';
import { normalizeVisibleDays, normalizeVisibleHours } from 'src/app/components/vehicles/timelineAxis';
import { APP } from 'src/app/config/app';
import {
  deserializeVehiclesCollapsedRows,
  deserializeVehiclesFilters,
  deserializeVehiclesGroupBy,
  deserializeVehiclesSelection,
  deserializeVehiclesViewMode,
  deserializeVehiclesViewOptions,
  sameVehiclesFilters,
  serializeVehiclesCollapsedRows,
  serializeVehiclesFilters,
  serializeVehiclesGroupBy,
  serializeVehiclesViewMode,
  serializeVehiclesViewOptions,
} from 'src/app/filters';
import {
  IScheduleCollapsedRows,
  IScheduleViewOptions,
  ITimelineVisibility,
  IUnsavedChangesGuarded,
  IUserDriver,
  IVehicle,
  IVehiclePayload,
  IVehicleScheduleFilters,
} from 'src/app/models';
import { NotificationTypes, ScheduleGroupBy, ScheduleViewMode, UserType } from 'src/app/models/enums';
import {
  LiteralService,
  StorageService,
  UnsavedChangesService,
  UsersService,
  UtilsService,
  VehiclesService,
} from 'src/app/services';
import { initialVehiclesState } from 'src/app/store/initialisation/vehicles';
import { images } from 'src/images';

@Component({
  selector: 'app-vehicles',
  templateUrl: './vehicles.component.html',
  styleUrls: ['./vehicles.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VehiclesComponent implements IUnsavedChangesGuarded {
  @ViewChild(VehicleScheduleComponent) schedule?: VehicleScheduleComponent;

  zoom = signal(0);
  visibleHours = signal(APP.SCHEDULE_ALL_HOURS);
  visibleDays = signal(APP.SCHEDULE_ALL_WEEKDAYS);
  searchValue = signal('');
  private storedViewOptions = signal(APP.SCHEDULE_DEFAULT_VIEW_OPTIONS);
  private queryParams = toSignal(this.activatedRoute.queryParamMap, { requireSync: true });
  viewOptions = computed(() => deserializeVehiclesViewOptions(this.queryParams()) ?? this.storedViewOptions());
  collapsedRows = computed(() => deserializeVehiclesCollapsedRows(this.queryParams()));
  private routeParams = toSignal(this.activatedRoute.paramMap, { requireSync: true });
  filters = computed(() => deserializeVehiclesFilters(this.queryParams()), { equal: sameVehiclesFilters });
  viewMode = computed(() => deserializeVehiclesViewMode(this.queryParams()));
  groupBy = computed(() => deserializeVehiclesGroupBy(this.queryParams()));
  selectedRowId = computed(() => deserializeVehiclesSelection(this.routeParams()));
  private selectionRequest = 0;

  selectedDriver = signal<IUserDriver | undefined>(undefined);
  selectedVehicle = signal<IVehicle | undefined>(undefined);
  newVehicle = signal<IVehicle | undefined>(undefined);

  public images = images;
  public deactivateVehicleModal: Modal;
  public deactivateVehicleShowed = false;

  constructor(
    private vehiclesService: VehiclesService,
    private utilsService: UtilsService,
    public literalService: LiteralService,
    private usersService: UsersService,
    private storageService: StorageService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private unsavedChangesService: UnsavedChangesService,
    destroyRef: DestroyRef,
  ) {
    destroyRef.onDestroy(this.unsavedChangesService.register(() => !!this.schedule?.unsavedChanges()));
    effect(() => {
      const groupBy = this.groupBy();
      const id = this.selectedRowId();
      untracked(() => this.showSelection(groupBy, id));
    });
  }

  async ngOnInit() {
    this.literalService.setTitle('sidebar.vehicles', ' - NEMI Backoffice');
    this.storageService.setTitleKey('vehicles.fleetManagement');
    const [storedHours, storedDays, storedViewOptions] = await Promise.all([
      this.storageService.getVehiclesTimelineHours(),
      this.storageService.getVehiclesTimelineDays(),
      this.storageService.getVehiclesViewOptions(),
    ]);
    if (storedViewOptions) {
      this.storedViewOptions.set(normalizeViewOptions(storedViewOptions));
    }
    if (storedHours) {
      this.visibleHours.set(normalizeVisibleHours(storedHours));
    }
    if (storedDays) {
      this.visibleDays.set(normalizeVisibleDays(storedDays));
    }
  }

  ngOnDestroy() {
    this.storageService.setTitleKey(null);
  }

  confirmLeave(nextUrl: string): boolean | Promise<boolean> {
    if (this.utilsService.isUrlUnderPath(nextUrl, APP.VEHICLES_PATH)) return true;
    return this.unsavedChangesService.confirmDiscard('vehicles.actions.leaveSchedule.description');
  }

  async onChangeTimelineVisibility({ hours, days }: ITimelineVisibility) {
    this.visibleHours.set(hours);
    this.visibleDays.set(days);
    await Promise.all([this.storageService.setVehiclesTimelineHours(hours), this.storageService.setVehiclesTimelineDays(days)]);
  }

  async onChangeViewOptions(viewOptions: IScheduleViewOptions) {
    this.storedViewOptions.set(viewOptions);
    this.mergeQueryParams(serializeVehiclesViewOptions(viewOptions));
    await this.storageService.setVehiclesViewOptions(viewOptions);
  }

  async onChangeCollapsedRows(collapsedRows: IScheduleCollapsedRows) {
    this.mergeQueryParams(serializeVehiclesCollapsedRows(collapsedRows));
    await this.storageService.setVehiclesCollapsedRows(collapsedRows);
  }

  goToNow() {
    this.schedule?.goToNow();
  }

  onFilter(filters: IVehicleScheduleFilters) {
    this.clearDetails();
    this.mergeQueryParams(serializeVehiclesFilters(filters), APP.VEHICLES_PATH);
  }

  onChangeViewMode(viewMode: ScheduleViewMode) {
    this.mergeQueryParams(serializeVehiclesViewMode(viewMode));
  }

  onChangeGroupBy(groupBy: ScheduleGroupBy) {
    this.clearDetails();
    const noCollapsedRows: IScheduleCollapsedRows = { collapsed: [], expanded: [] };
    this.mergeQueryParams(
      { ...serializeVehiclesGroupBy(groupBy), ...serializeVehiclesCollapsedRows(noCollapsedRows) },
      APP.VEHICLES_PATH,
    );
    this.storageService.setVehiclesCollapsedRows(noCollapsedRows);
  }

  private mergeQueryParams(queryParams: Params, path?: string) {
    if (path) {
      this.router.navigate([path], { queryParams, queryParamsHandling: 'merge' });
    } else {
      this.router.navigate([], { relativeTo: this.activatedRoute, queryParams, queryParamsHandling: 'merge' });
    }
  }

  private showRow(id?: number) {
    const commands = id === undefined ? [APP.VEHICLES_PATH] : [APP.VEHICLES_PATH, id];
    this.router.navigate(commands, { queryParamsHandling: 'preserve' });
  }

  onNewVehicle() {
    this.newVehicle.set(initialVehiclesState);
  }

  onSelectRow(id: number) {
    this.newVehicle.set(undefined);
    this.showRow(id);
  }

  private async showSelection(groupBy: ScheduleGroupBy, id?: number) {
    const request = ++this.selectionRequest;
    if (id === undefined) {
      this.selectedVehicle.set(undefined);
      this.selectedDriver.set(undefined);
      return;
    }
    this.newVehicle.set(undefined);
    try {
      if (groupBy === ScheduleGroupBy.VEHICLES) {
        await this.showVehicle(id, request);
      } else {
        await this.showDriver(id, request);
      }
    } catch {
      if (request === this.selectionRequest) {
        this.closeDetails();
      }
    }
  }

  private async showVehicle(id: number, request: number) {
    this.selectedDriver.set(undefined);
    if (this.selectedVehicle()?.id === id) {
      return;
    }
    const vehicleDetail = await this.vehiclesService.getVehicle(id);
    if (request === this.selectionRequest) {
      this.selectedVehicle.set(this.toVehicleDetail(vehicleDetail));
    }
  }

  private async showDriver(id: number, request: number) {
    this.selectedVehicle.set(undefined);
    if (this.selectedDriver()?.id === id) {
      return;
    }
    const driverDetail = await this.usersService.getUser(UserType.Driver, id);
    if (request === this.selectionRequest) {
      this.selectedDriver.set(driverDetail);
    }
  }

  private toVehicleDetail(vehicle: any): IVehicle {
    const convertibleSeats = vehicle?.convertibleSeats ?? [];
    return {
      ...(vehicle as IVehicle),
      prmAvailableSeats: convertibleSeats.filter((seat: any) => seat.nregular === 0).length,
      convertibleSeats: convertibleSeats
        .filter((seat: any) => seat.nregular !== 0)
        .map((seat: any) => ({ numberOfRegularSeats: seat.nregular })),
    };
  }

  async onDriverEdited(driver: IUserDriver) {
    this.selectedDriver.set(driver);
    await this.schedule?.reload();
  }

  closeDetails() {
    this.clearDetails();
    if (this.selectedRowId() !== undefined) {
      this.showRow();
    }
  }

  private clearDetails() {
    this.selectedVehicle.set(undefined);
    this.newVehicle.set(undefined);
    this.selectedDriver.set(undefined);
  }

  async onChangeStatus(active: boolean) {
    if (!active) {
      this.showDeactivateVehicleModal();
      return;
    }
    await this.updateVehicle({ active });
  }

  showDeactivateVehicleModal() {
    this.deactivateVehicleModal = new Modal(document.getElementById('deactivateVehicle'), {
      placement: 'center',
      closable: true,
      onHide: () => { this.deactivateVehicleShowed = false; },
      onShow: () => { this.deactivateVehicleShowed = true; },
    });
    this.deactivateVehicleModal.toggle();
  }

  async deactivateVehicleAction() {
    this.deactivateVehicleModal.hide();
    await this.updateVehicle({ active: false });
  }

  async onSaveVehicle(vehicle: IVehiclePayload) {
    if (this.newVehicle()) {
      await this.createVehicle(vehicle);
    } else {
      await this.updateVehicle(vehicle);
    }
  }

  private async createVehicle(vehicle: IVehiclePayload) {
    try {
      const created = await this.vehiclesService.createVehicle(vehicle);
      this.newVehicle.set(undefined);
      const createdDetail = this.toVehicleDetail(created);
      this.selectedVehicle.set(createdDetail);
      if (this.groupBy() === ScheduleGroupBy.VEHICLES) {
        this.showRow(createdDetail.id);
      }
      this.utilsService.showNotification(images.notifications.check, 'vehicles.actions.create', NotificationTypes.SUCCESS, 'success.title', 'success.text');
      await this.schedule?.reload();
    } catch {
      this.utilsService.showNotification(images.notifications.warning, 'vehicles.actions.create', NotificationTypes.DANGER, 'error.title', 'error.text');
    }
  }

  private async updateVehicle(vehicle: IVehiclePayload) {
    const vehicleId = this.selectedVehicle()?.id;
    if (!vehicleId) return;
    try {
      const updated = await this.vehiclesService.updateVehicle(vehicleId, vehicle);
      this.selectedVehicle.set(this.toVehicleDetail(updated));
      this.utilsService.showNotification(images.notifications.check, 'vehicles.actions.edit', NotificationTypes.SUCCESS, 'success.title', 'success.text');
      await this.schedule?.reload();
    } catch {
      this.utilsService.showNotification(images.notifications.warning, 'vehicles.actions.edit', NotificationTypes.DANGER, 'error.title', 'error.text');
    }
  }

}
